/**
 * Public Privacy Policy — linked from the advertising platform API applications
 * and OAuth consent screens. Renders with no auth and no network calls.
 */

import React from 'react';
import { PolicyPage, type PolicySection } from './PolicyPage';
import { COMPANY_NAME, PRODUCT_NAME, CONTACT_EMAIL, SITE_URL } from './PublicPageLayout';

const SECTIONS: PolicySection[] = [
  {
    id: 'scope',
    heading: 'Scope',
    body: (
      <p>
        This policy covers {PRODUCT_NAME}, the advertising reporting platform operated by {COMPANY_NAME}
        {' '}at <a href={SITE_URL}>{SITE_URL}</a>. {PRODUCT_NAME} is an invitation-only platform used by
        {' '}{COMPANY_NAME} staff and by the clients whose advertising accounts we manage.
      </p>
    ),
  },
  {
    id: 'collect',
    heading: 'Information we collect',
    body: (
      <>
        <p>
          <strong>Account information.</strong> The name and email address of each invited user, the
          organization they belong to, and their role. We use this only to authenticate users and to
          determine which organization's data they may view.
        </p>
        <p>
          <strong>Advertising platform data.</strong> When a client connects or authorizes an advertising
          account (Google Ads, Meta, Pinterest, and similar platforms), we retrieve reporting data for that
          account: campaign, ad group, ad, and creative names and identifiers, spend, impressions, clicks,
          conversions, conversion values, and related performance metrics, along with creative previews and
          thumbnails. We request read-only access wherever the platform offers it.
        </p>
        <p>
          <strong>Usage and diagnostic data.</strong> We collect application logs, error reports, and basic
          product-analytics events (such as which pages are viewed) to keep the platform working and to
          diagnose failures.
        </p>
        <p>
          We do not collect payment card data, government identifiers, health information, or the personal
          data of our clients' end customers.
        </p>
      </>
    ),
  },
  {
    id: 'use',
    heading: 'How we use information',
    body: (
      <>
        <p>
          We use the information above solely to operate {PRODUCT_NAME}: to authenticate users, to generate
          advertising performance reports and analysis for the organization that owns the data, to support
          and maintain the platform, and to meet our contractual obligations to our clients.
        </p>
        <p>
          We do not use advertising platform data for our own advertising or marketing, we do not sell it,
          and we do not use it to train machine learning or artificial intelligence models.
        </p>
      </>
    ),
  },
  {
    id: 'google',
    heading: 'Google user data and Limited Use',
    body: (
      <>
        <div className="ac-callout">
          <p>
            {PRODUCT_NAME}'s use and transfer of information received from Google APIs adheres to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noreferrer noopener"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>
        </div>
        <p>
          Specifically: data obtained from Google Ads and related Google APIs is used only to provide and
          improve reporting features visible to the account owner; it is not transferred to others except as
          necessary to provide those features, for security purposes, or to comply with applicable law; it is
          not used for advertising; and it is not read by humans except with the account owner's explicit
          consent, for security purposes, to comply with applicable law, or where the data has been
          aggregated and anonymized for internal operations.
        </p>
      </>
    ),
  },
  {
    id: 'sharing',
    heading: 'Sharing and subprocessors',
    body: (
      <>
        <p>
          We share data only with service providers that host or support the platform, under contracts that
          restrict their use of it. These currently include our cloud hosting and application-hosting
          providers, our managed database and authentication provider, and our error-monitoring, logging, and
          product-analytics providers. We do not sell data or share it with advertisers or data brokers.
        </p>
        <p>
          We may disclose information if legally required to do so, or where necessary to investigate abuse
          or protect the security of the platform.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    heading: 'Data retention and deletion',
    body: (
      <>
        <p>
          Advertising reporting data is retained for as long as the client relationship is active, so that
          historical performance comparisons remain available. When an engagement ends, or on written request
          from the account owner, we revoke the platform connections and delete or anonymize that
          organization's data within 90 days, except where we are required to retain records by law.
        </p>
        <p>
          Users may request access to, correction of, or deletion of their account information at any time by
          emailing <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    heading: 'Security',
    body: (
      <p>
        Access to {PRODUCT_NAME} requires an account created by invitation. Data is transmitted over TLS and
        stored with row-level access controls so that each user can reach only the organizations they have
        been granted access to. Platform credentials and API tokens are stored as encrypted secrets and are
        rotated when staff access changes.
      </p>
    ),
  },
  {
    id: 'children',
    heading: 'Children',
    body: (
      <p>{PRODUCT_NAME} is a business tool and is not directed to, or intended for use by, anyone under 18.</p>
    ),
  },
  {
    id: 'changes',
    heading: 'Changes to this policy',
    body: (
      <p>
        If we make material changes to this policy we will update the date at the top of this page and, where
        the change affects how client data is handled, notify the affected organizations directly.
      </p>
    ),
  },
];

export default function PrivacyPage() {
  return (
    <PolicyPage
      eyebrow="Legal"
      title="Privacy Policy"
      intro={`How ${COMPANY_NAME} handles data in ${PRODUCT_NAME} — what we collect, what we do with it, and what we will never do with it.`}
      sections={SECTIONS}
    />
  );
}
